import { Timestamp } from 'firebase/firestore';
import { format, isValid, parseISO } from 'date-fns';

export const formatDate = (date: Date | Timestamp | null | undefined): string => {
  if (!date) return 'Date not set';
  
  const dateObj = date instanceof Timestamp ? date.toDate() : date;
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDateTime = (date: Date | Timestamp | null | undefined): string => {
  if (!date) return 'Date/time not set';
  
  const dateObj = date instanceof Timestamp ? date.toDate() : date;
  return dateObj.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export const formatTime = (date: Date | Timestamp | string | null | undefined): string => {
  if (!date) return 'Time not set';
  
  try {
    // Convert Timestamp to Date if needed
    let dateObj: Date;
    
    if (date instanceof Timestamp) {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof (date as any).toDate === 'function') {
      // Handle Firestore Timestamp-like objects
      dateObj = (date as any).toDate();
    } else {
      console.warn('Invalid date format:', date);
      return 'Invalid time';
    }
    
    // Check if dateObj is a valid date
    if (isNaN(dateObj.getTime())) {
      return 'Invalid time';
    }
    
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Time format error';
  }
};

export const getEventStatus = (startDateTime: Timestamp | null | undefined, endDateTime: Timestamp | null | undefined): 'upcoming' | 'ongoing' | 'past' | 'unknown' => {
  if (!startDateTime || !endDateTime) return 'unknown';
  
  const now = new Date();
  const start = startDateTime.toDate();
  const end = endDateTime.toDate();

  if (now < start) {
    return 'upcoming';
  } else if (now >= start && now <= end) {
    return 'ongoing';
  } else {
    return 'past';
  }
};

export const isValidDateRange = (startDateTime: Date | Timestamp | null | undefined, endDateTime: Date | Timestamp | null | undefined): boolean => {
  if (!startDateTime || !endDateTime) return false;
  
  const start = startDateTime instanceof Timestamp ? startDateTime.toDate() : startDateTime;
  const end = endDateTime instanceof Timestamp ? endDateTime.toDate() : endDateTime;
  
  return start < end;
};

export const getRecurringDates = (
  startDate: Date,
  endDate: Date,
  recurringType: 'daily' | 'weekly' | 'monthly'
): Date[] => {
  const dates: Date[] = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    
    switch (recurringType) {
      case 'daily':
        currentDate.setDate(currentDate.getDate() + 1);
        break;
      case 'weekly':
        currentDate.setDate(currentDate.getDate() + 7);
        break;
      case 'monthly':
        currentDate.setMonth(currentDate.getMonth() + 1);
        break;
    }
  }

  return dates;
};

/**
 * A robust date formatting utility that safely handles multiple date types.
 * @param date - The date to format (Timestamp, Date object, or ISO string).
 * @param formatStr - The desired date format string (defaults to 'PPP').
 * @returns The formatted date string, or a fallback string if the date is invalid.
 */
export const safeFormatDate = (
  date: Timestamp | Date | string | undefined | null,
  formatStr: string = 'PPP'
): string => {
  if (!date) {
    return 'Date not set';
  }

  let dateObj: Date;

  try {
    if (date instanceof Timestamp) {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = parseISO(date);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      return 'Invalid date type';
    }

    if (!isValid(dateObj)) {
      // Attempt to parse number-like strings that parseISO might miss
      if (typeof date === 'string' && !isNaN(Number(date))) {
          dateObj = new Date(Number(date));
          if(isValid(dateObj)) return format(dateObj, formatStr);
      }
      return 'Invalid date value';
    }

    return format(dateObj, formatStr);
  } catch (error) {
    console.error('Error formatting date:', { date, error });
    return 'Date format error';
  }
}; 